import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function convertNameToHyphenatedLowerCase(name: string) {
  return name?.replace(/\s+/g, '-').toLowerCase()?.trim();
}

export function gbToBytes(gb: number): number {
  return gb * 1024 * 1024 * 1024;
}

export function bytesToGb(bytes: number): number {
  return bytes / 1024 ** 3;
}

export function bytesToMb(bytes: number): number {
  return bytes / 1024 ** 2;
}

export function findAvailableStorage(
  totalStorage: number,
  usedStorage: number
) {
  const freeStorage = gbToBytes(totalStorage) - usedStorage;
  return freeStorage > 0 ? freeStorage : 0;
}

export function roundToDecimals(value: number, decimals: number = 2): number {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}

export const hasNumber = (value: unknown): value is number =>
  typeof value === 'number' && !Number.isNaN(value);

export const hasText = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

export const stringOrEmpty = (value: unknown) =>
  hasText(value) ? value.trim() : '';

export const numberOrNull = (value: unknown): number | null =>
  hasNumber(value) ? value : null;

export const formatTrend = (value: unknown) =>
  hasNumber(value) ? `${value >= 0 ? '▲' : '▼'} ${value}%` : '';

export const trendColor = (value: unknown, inverted = false) => {
  if (!hasNumber(value)) {
    return 'text-gray-500';
  }
  const positiveOutcome = inverted ? value < 0 : value >= 0;
  return positiveOutcome ? 'text-green-600' : 'text-red-600';
};

export const formatShortDate = (value?: string) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date
    .toLocaleDateString('en-US', { month: 'short', day: '2-digit' })
    .replace(',', '');
};

export const normalizeDayOfWeek = (value?: string) => {
  const normalized = (value || '').toLowerCase();

  if (normalized.startsWith('mon')) return 'mon';
  if (normalized.startsWith('tue')) return 'tue';
  if (normalized.startsWith('wed')) return 'wed';
  if (normalized.startsWith('thu')) return 'thu';
  if (normalized.startsWith('fri')) return 'fri';
  if (normalized.startsWith('sat')) return 'sat';
  if (normalized.startsWith('sun')) return 'sun';

  return '';
};

export const normalizeHour = (value?: string) => {
  const parsed = Number(value);

  if (Number.isNaN(parsed) || parsed < 0 || parsed > 23) {
    return null;
  }

  return String(parsed).padStart(2, '0');
};

export const formatSessionTime = (summary?: any) => {
  const minutes = summary?.avgSessionTime?.minutes;
  const seconds = summary?.avgSessionTime?.seconds;

  if (typeof minutes === 'number' && typeof seconds === 'number') {
    return `${minutes}m ${seconds}s`;
  }

  const valueSeconds = summary?.avgSessionTime?.valueSeconds;
  if (!hasNumber(valueSeconds)) {
    return '-';
  }
  const computedMinutes = Math.floor(valueSeconds / 60);
  const computedSeconds = valueSeconds % 60;
  return `${computedMinutes}m ${computedSeconds}s`;
};

type SettingRowLike = {
  id?: string;
  name?: string;
  value?: unknown;
  status?: string;
  createdBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

const normalizeSettingKey = (value: unknown) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

export const findSettingRowByNames = <T extends SettingRowLike>(
  rows: T[] | undefined,
  names: string[]
) => {
  if (!Array.isArray(rows) || names.length === 0) {
    return undefined;
  }

  const normalizedNames = names.map(normalizeSettingKey);
  return rows.find(row =>
    normalizedNames.includes(normalizeSettingKey(row?.name))
  );
};

export const getSettingValueByNames = <T extends SettingRowLike>(
  rows: T[] | undefined,
  names: string[]
) => findSettingRowByNames(rows, names)?.value;

export const settingValueToString = (value: unknown) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return '';
};

export function getValueFromArray<T>(
  array: T[] | undefined,
  key: keyof T,
  value: unknown
): T | undefined;

export function getValueFromArray<T, K extends keyof T>(
  array: T[] | undefined,
  key: keyof T,
  value: unknown,
  returnValue: K
): T[K] | undefined;

export function getValueFromArray<T, K extends keyof T>(
  array: T[] | undefined,
  key: keyof T,
  value: unknown,
  returnValue?: K
) {
  if (!array) return undefined;

  const foundItem = array.find(item => item[key] === value);

  if (returnValue && foundItem) {
    return foundItem[returnValue];
  }

  return foundItem;
}

export function getInitials(name: string | undefined) {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  const initials = parts
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('');
  return initials;
}

export function getBgColorByGender(gender: string | undefined) {
  const avatarBg =
    gender?.toLowerCase() === 'male'
      ? '#93C5FD'
      : gender?.toLowerCase() === 'female'
        ? '#F9A8D4'
        : '#C7B3E5';
  return avatarBg;
}
